import { useMemo } from "react";

import LockIcon from "@mui/icons-material/LockOutlined";
import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import { addWeeks, getISOWeek, getISOWeekYear, startOfISOWeek, subWeeks } from "date-fns";

import { AttendanceOverviewEntryResponse, AttendanceOverviewRowResponse } from "@/api";
import { CalendarNavigation } from "@/common/components/calendar/components/calendar-header/components/navigation/CalendarNavigation";
import { useCounselors } from "@/common/components/counselor-select/hooks/useCounselors";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { formatCounselorLabel } from "@/common/utils/formatCounselorLabel";
import {
  ALL_FILTER_VALUE,
  getAppointmentEntries,
  getAttendanceEntries,
} from "@/pages/attendance-list/AttendanceList.utils";
import { useTranslation } from "@/translations";

interface Props {
  weekStart: Date;
  rows: AttendanceOverviewRowResponse[];
  selectedCounselorId: string;
  selectedAppointmentPurposeKey: string;
  selectedAttendancePurposeKey: string;
  selectedLocation: string;
  onWeekStartChange: (weekStart: Date) => void;
  onSelectedCounselorIdChange: (counselorId: string) => void;
  onSelectedAppointmentPurposeKeyChange: (purposeKey: string) => void;
  onSelectedAttendancePurposeKeyChange: (purposeKey: string) => void;
  onSelectedLocationChange: (location: string) => void;
}

export function AttendanceListHeader({
  weekStart,
  rows,
  selectedCounselorId,
  selectedAppointmentPurposeKey,
  selectedAttendancePurposeKey,
  selectedLocation,
  onWeekStartChange,
  onSelectedCounselorIdChange,
  onSelectedAppointmentPurposeKeyChange,
  onSelectedAttendancePurposeKeyChange,
  onSelectedLocationChange,
}: Props) {
  const { t } = useTranslation();
  const { purposeLabelOf } = useAppointmentLabels();
  const { userCodeOf } = useCounselors();

  const counselorFilterOptions = useMemo(
    () =>
      rows
        .slice()
        .sort((a, b) => a.userCode.localeCompare(b.userCode))
        .map((row) => ({ value: row.counselorId, label: formatCounselorLabel(row) })),
    [rows]
  );

  // Only the Zwecke and Orte the displayed week actually holds are offered — a value nothing matches
  // would only ever empty the table.
  const appointmentPurposeOptions = useMemo(
    () => collectOptions(rows, getAppointmentEntries, (entry) => entry.purposeKey, purposeLabelOf),
    [rows, purposeLabelOf]
  );

  const attendancePurposeOptions = useMemo(
    () => collectOptions(rows, getAttendanceEntries, (entry) => entry.purposeKey, purposeLabelOf),
    [rows, purposeLabelOf]
  );

  const locationOptions = useMemo(() => collectOptions(rows, getAppointmentEntries, (entry) => entry.location), [rows]);

  return (
    <Stack spacing={2}>
      <Stack spacing={1.5}>
        <Typography variant="h1">{t("attendanceList.title")}</Typography>
        <Box display="flex" alignItems="center" gap={0.75}>
          <LockIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            {t("attendanceList.readOnlyNote")}
          </Typography>
        </Box>
      </Stack>
      {/* No role="toolbar": ARIA expects a toolbar to be one tab stop with arrow-key navigation between
          its controls, which this is not. As a plain box the buttons and the filters are announced and
          reached with Tab, exactly as they behave. */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          {/* CalendarNavigation fills its parent's height, so it needs a bounded box to match the 32px filter Select. */}
          <Box height={32}>
            <CalendarNavigation
              onPrevClick={() => onWeekStartChange(subWeeks(weekStart, 1))}
              onNextClick={() => onWeekStartChange(addWeeks(weekStart, 1))}
              onTodayClick={() => onWeekStartChange(startOfISOWeek(new Date()))}
              previousLabel={t("calendar.previousWeek")}
              nextLabel={t("calendar.nextWeek")}
            />
          </Box>
          <Typography variant="body1" fontWeight={700}>
            {t("attendanceList.weekLabel", { week: getISOWeek(weekStart), year: getISOWeekYear(weekStart) })}
          </Typography>
        </Box>
        {/* Fixed order: Termine → Anwesenheiten → Orte → Berater:innen. */}
        <Box display="flex" alignItems="center" gap={2}>
          <FilterSelect
            value={selectedAppointmentPurposeKey}
            options={appointmentPurposeOptions}
            allLabel={t("attendanceList.filter.allAppointmentPurposes")}
            ariaLabel={t("attendanceList.filter.appointmentPurposeAriaLabel")}
            renderSelected={purposeLabelOf}
            onChange={onSelectedAppointmentPurposeKeyChange}
          />
          <FilterSelect
            value={selectedAttendancePurposeKey}
            options={attendancePurposeOptions}
            allLabel={t("attendanceList.filter.allAttendancePurposes")}
            ariaLabel={t("attendanceList.filter.attendancePurposeAriaLabel")}
            renderSelected={purposeLabelOf}
            onChange={onSelectedAttendancePurposeKeyChange}
          />
          <FilterSelect
            value={selectedLocation}
            options={locationOptions}
            allLabel={t("attendanceList.filter.allLocations")}
            ariaLabel={t("attendanceList.filter.locationAriaLabel")}
            renderSelected={(location) => location}
            onChange={onSelectedLocationChange}
          />
          <FilterSelect
            value={selectedCounselorId}
            options={counselorFilterOptions}
            allLabel={t("attendanceList.filter.allCounselors")}
            ariaLabel={t("attendanceList.filter.label")}
            // The closed control shows the bare Kürzel; the open list spells the name out.
            renderSelected={(counselorId) => userCodeOf(counselorId) ?? ""}
            onChange={onSelectedCounselorIdChange}
          />
        </Box>
      </Box>
    </Stack>
  );
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  value: string;
  options: FilterOption[];
  allLabel: string;
  ariaLabel: string;
  /**
   * Label of the closed control. Resolved from the value rather than looked up in the options,
   * because a selection survives a week change: the value can outlive its entry in the list.
   */
  renderSelected: (value: string) => string;
  onChange: (value: string) => void;
}

/**
 * One toolbar filter. None of the four carries a visible label — the closed control is the label —
 * so each needs an aria-label of its own to stay reachable by screen reader.
 */
function FilterSelect({ value, options, allLabel, ariaLabel, renderSelected, onChange }: FilterSelectProps) {
  return (
    <Select
      size="small"
      variant="outlined"
      value={value}
      displayEmpty
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ "aria-label": ariaLabel }}
      renderValue={(selected) => (selected === ALL_FILTER_VALUE ? allLabel : renderSelected(selected))}
      sx={{
        minWidth: 180,
        maxHeight: 32,
        bgcolor: "common.white",
        color: "text.primary",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "action.active",
          borderWidth: "1px",
        },
      }}
    >
      <MenuItem value={ALL_FILTER_VALUE}>{allLabel}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * The distinct values one field of the week's entries takes, labelled and sorted by label. Which
 * entries of a row are considered (its Termine or its Anwesenheiten) is up to the caller, so the
 * Termine and Anwesenheiten filters can never offer one another's Zwecke. Entries without a value
 * (the Ort is optional on an appointment) contribute no option.
 */
function collectOptions(
  rows: AttendanceOverviewRowResponse[],
  entriesOf: (row: AttendanceOverviewRowResponse) => AttendanceOverviewEntryResponse[],
  select: (entry: AttendanceOverviewEntryResponse) => string | null,
  // Omitted where the column already holds the German label, as the Ort's does.
  labelOf: (key: string) => string = (key) => key
): FilterOption[] {
  const keys = new Set<string>();
  for (const row of rows) {
    for (const entry of entriesOf(row)) {
      const key = select(entry);
      if (key) {
        keys.add(key);
      }
    }
  }
  return [...keys].map((key) => ({ value: key, label: labelOf(key) })).sort((a, b) => a.label.localeCompare(b.label));
}
